import type { ComponentType } from 'svelte';
import type { Readable } from 'svelte/store';

export type MainLayoutModule = { default: ComponentType };
export type MainLayoutImport = () => Promise<MainLayoutModule>;

interface MainLayoutImporters {
	classic: MainLayoutImport;
	new: MainLayoutImport;
}

export interface MainLayoutLoadState {
	component?: ComponentType;
	error?: unknown;
	loading: boolean;
}

const defaultImporters: MainLayoutImporters = {
	classic: () => import('./MainLayout.svelte'),
	new: () => import('./MainLayoutNew.svelte'),
};

export const createCachedMainLayoutLoader = (importers: MainLayoutImporters) => {
	const cache = new Map<keyof MainLayoutImporters, Promise<MainLayoutModule>>();

	const load = (useNewLayout: boolean) => {
		const key = useNewLayout ? 'new' : 'classic';
		const cached = cache.get(key);
		if (cached) return cached;

		const pending = importers[key]().catch((error) => {
			if (cache.get(key) === pending) cache.delete(key);
			throw error;
		});
		cache.set(key, pending);
		return pending;
	};

	return { load };
};

export const createLatestLayoutSelection = (
	load: (useNewLayout: boolean) => Promise<MainLayoutModule>,
	onState: (state: MainLayoutLoadState) => void,
) => {
	let requestId = 0;
	let selectedLayout = false;

	const select = async (useNewLayout: boolean) => {
		selectedLayout = useNewLayout;
		const currentRequestId = ++requestId;
		onState({ loading: true });

		try {
			const module = await load(useNewLayout);
			if (currentRequestId !== requestId) return;
			onState({ component: module.default, loading: false });
		} catch (error) {
			if (currentRequestId !== requestId) return;
			onState({ error, loading: false });
		}
	};

	return {
		select,
		retry: () => select(selectedLayout),
	};
};

export const bindMainLayoutSelection = (
	showNewLayout: Readable<boolean>,
	selection: ReturnType<typeof createLatestLayoutSelection>,
) => showNewLayout.subscribe((useNewLayout) => void selection.select(useNewLayout));

export const mainLayoutLoader = createCachedMainLayoutLoader(defaultImporters);
